import { InteractionRequiredAuthError } from '@azure/msal-browser';
import axios from 'axios';

import { msalInstance } from '@/main';

import {
	clearTokenCache,
	forceTokenRefresh,
	getAccessTokenRequest,
	getUserTokenToApiRequest,
	validateUserAccess,
} from './msal.service';

const baseURL = import.meta.env.VITE_SGFT_URL;

const api = axios.create({
	baseURL,
	headers: {
		'Content-type': 'application/json',
	},
});

// Request interceptor to add auth token and content-type
api.interceptors.request.use(
	async (config: any) => {
		try {
			// Validate user access before making request
			if (!validateUserAccess()) {
				throw new Error('User access validation failed');
			}

			// Set content-type to application/json only if not FormData
			if (config.data instanceof FormData) {
				config.headers['Content-Type'] = 'multipart/form-data';
			}

			return await getUserTokenToApiRequest(config);
		} catch (error) {
			// If token acquisition fails, proceed without token
			// The response interceptor will handle the error
			return config;
		}
	},
	(error) => {
		return Promise.reject(error);
	},
);

// Response interceptor to handle token refresh and access revocation
api.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		// Handle 401 (Unauthorized) - token expired or invalid
		if (error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			try {
				// Clear the cached token
				clearTokenCache();

				// Validate user access first
				if (!validateUserAccess()) {
					throw new Error('User no longer has access');
				}

				// Try to acquire a new token silently
				await forceTokenRefresh();

				// Retry the original request with new token
				const configWithNewToken =
					await getUserTokenToApiRequest(originalRequest);
				return api(configWithNewToken);
			} catch (tokenError) {
				// If silent token acquisition fails, redirect to login
				if (tokenError instanceof InteractionRequiredAuthError) {
					msalInstance.acquireTokenRedirect(getAccessTokenRequest());
				} else {
					// User access validation failed or other error - force logout
					console.error('Token refresh failed, forcing logout:', tokenError);
					msalInstance.logoutRedirect();
				}
				return Promise.reject(tokenError);
			}
		}

		// Handle 403 (Forbidden) - user doesn't have permission
		if (error.response?.status === 403) {
			console.warn(
				'Access denied - user may have been removed from required groups',
			);
			// Don't retry 403 errors, but log for monitoring
			// Optionally redirect to login or show access denied page
			clearTokenCache();
		}

		return Promise.reject(error);
	},
);

export default api;
